import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { TwoFactorMethod } from "generated/graphql";
import React, { ChangeEvent, FormEvent } from "react";

interface TwoFactorCodeInputProps {
    verificationCode: string;
    onCodeChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onSubmit: (e: FormEvent<HTMLFormElement>) => void;
    onResend: () => void;
    onBack?: () => void;
    selectedMethod: TwoFactorMethod | null;
    resendCountdown: number;
    loading?: boolean;
    submitButtonText?: string;
    backButtonText?: string;
    contextText?: string;
}

export const TwoFactorCodeInput: React.FC<TwoFactorCodeInputProps> = ({
    verificationCode,
    onCodeChange,
    onSubmit,
    onResend,
    onBack,
    selectedMethod,
    resendCountdown,
    loading = false,
    submitButtonText = "Verify",
    backButtonText = "Back",
    contextText,
}) => {
    const getDefaultContextText = () => {
        const methodText = selectedMethod === TwoFactorMethod.Sms ? "phone" : "email";
        return `We've sent a verification code to your ${methodText}. Please enter it below.`;
    };

    return (
        <div>
            <div className="mb-4 text-gray-600">
                {contextText || getDefaultContextText()}
            </div>

            <form onSubmit={onSubmit}>
                <div className="space-y-4">
                    <Input
                        type="number"
                        id="verificationCode"
                        value={verificationCode}
                        onChange={onCodeChange}
                        label="Verification Code"
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        disabled={loading}
                    />

                    <div className="flex flex-col space-y-4">
                        <PrimaryButton
                            type="submit"
                            disabled={!verificationCode || loading}>
                            {loading ? "Verifying..." : submitButtonText}
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            onClick={onResend}
                            disabled={resendCountdown > 0 || loading}
                            className="text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed">
                            {resendCountdown > 0
                                ? `Resend Code (${resendCountdown}s)`
                                : "Resend Code"}
                        </SecondaryButton>
                        {onBack && (
                            <button
                                type="button"
                                onClick={onBack}
                                disabled={loading}
                                className={`text-sm ${defaultLinkStyle} disabled:text-gray-400`}>
                                {backButtonText}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};
